import { Bug, Flame, Lock, LockOpen, Plus, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useGetMatterQuery,
  usePatchMatterMutation,
} from '@/services/api/mattersService';

interface CoverageOverviewHeaderProps {
  onCreateCoverage: () => void;
  onDebugMentions: () => void;
  matterId?: string;
  onManageSharedCoverages?: () => void;
  showCrashoutToggle?: boolean;
  crashoutMode?: boolean;
  onToggleCrashout?: () => void;
}

export const CoverageOverviewHeader = ({
  onCreateCoverage,
  onDebugMentions,
  matterId,
  onManageSharedCoverages,
  showCrashoutToggle,
  crashoutMode,
  onToggleCrashout,
}: CoverageOverviewHeaderProps) => {
  const { data: matter } = useGetMatterQuery(matterId || '', {
    skip: !matterId,
  });
  const [locked, setLocked] = useState(false);
  const [patchMatter, { isLoading: isUpdating }] = usePatchMatterMutation();

  // Sync local state with matter data
  useEffect(() => {
    if (matter?.coverage_creation_locked !== undefined) {
      setLocked(matter.coverage_creation_locked);
    }
  }, [matter?.coverage_creation_locked]);

  const toggleLock = async () => {
    if (!matterId || isUpdating) return;
    try {
      await patchMatter({
        matter_id: matterId,
        patch: { coverage_creation_locked: !locked },
      }).unwrap();
      setLocked(!locked);
    } catch (e) {
      // no-op toast for now
    }
  };
  return (
    <div className="flex items-center justify-between">
      <div className="group flex items-center gap-2">
        <h1 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
          Insurance Coverage
          <Button
            variant="ghost"
            size="sm"
            onClick={onDebugMentions}
            className="text-gray-400 hover:text-gray-600 px-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            title="Debug coverage mentions"
          >
            <Bug className="h-4 w-4" />
          </Button>
        </h1>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {showCrashoutToggle && onToggleCrashout && (
          <Button
            onClick={onToggleCrashout}
            variant={crashoutMode ? 'outline' : 'ghost'}
            size="sm"
            className={
              crashoutMode
                ? 'gap-1.5 border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 text-xs h-8'
                : 'gap-1.5 text-gray-600 text-xs h-8'
            }
          >
            <Flame className="h-3.5 w-3.5" />
            {crashoutMode ? 'Exit Crashout' : 'Crashout Mode'}
          </Button>
        )}
        {matterId ? (
          <Button
            onClick={toggleLock}
            variant="ghost"
            size="sm"
            className="gap-1.5 text-gray-600 text-xs h-8"
            disabled={isUpdating}
          >
            {locked ? (
              <>
                <Lock className="h-3.5 w-3.5" /> Unlock new coverage
              </>
            ) : (
              <>
                <LockOpen className="h-3.5 w-3.5" /> Lock new coverage
              </>
            )}
          </Button>
        ) : null}
        {(matter?.companion_count ?? 0) > 1 && onManageSharedCoverages && (
          <Button
            onClick={onManageSharedCoverages}
            variant="ghost"
            size="sm"
            className="gap-1.5 text-gray-600 text-xs h-8"
          >
            <Users className="h-3.5 w-3.5" />
            Manage Shared Coverages
          </Button>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                onClick={onCreateCoverage}
                variant="outline"
                size="sm"
                className="gap-1.5 border-gray-300 text-gray-900 hover:bg-gray-50 text-xs h-8"
                disabled={locked}
              >
                <Plus className="h-3.5 w-3.5" />
                Add Coverage
              </Button>
            </span>
          </TooltipTrigger>
          {locked && (
            <TooltipContent>
              Coverage creation is locked. Unlock to add new coverage.
            </TooltipContent>
          )}
        </Tooltip>
      </div>
    </div>
  );
};
